/**
 * HealthRepository — health items, per-day taken logs (upsert on
 * (user_id, item_id, date)) and daily wellbeing (upsert on (user_id, date)).
 */
import { requireSupabase } from '@/lib/supabase/client'
import {
  dailyHealthFromRow,
  healthItemFromRow,
  healthItemToInsert,
  healthLogFromRow,
  type DailyHealthRow,
  type HealthItemRow,
  type HealthLogRow,
} from '@/lib/supabase/mappers'
import type { DailyHealth } from '@/types'
import type { HealthRepository } from '../interfaces'
import { assertRowReturned, fail, requireUserId } from './helpers'

export const healthRepository: HealthRepository = {
  async listItems() {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('health_items')
      .select('*')
      .eq('user_id', userId)
      .order('name', { ascending: true })
    if (error) fail(error)
    return ((data ?? []) as HealthItemRow[]).map(healthItemFromRow)
  },

  async createItem(input) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('health_items')
      .insert(healthItemToInsert(input, userId))
      .select()
      .single()
    return healthItemFromRow(assertRowReturned(data, error) as HealthItemRow)
  },

  async removeItem(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase.from('health_items').delete().eq('id', id).eq('user_id', userId)
    if (error) fail(error)
  },

  async listLogsForDate(date) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('health_logs')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
    if (error) fail(error)
    return ((data ?? []) as HealthLogRow[]).map(healthLogFromRow)
  },

  async setTaken(itemId, date, taken, time) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('health_logs')
      .upsert(
        {
          user_id: userId,
          item_id: itemId,
          date,
          taken,
          time: time ?? null,
        },
        { onConflict: 'user_id,item_id,date' },
      )
      .select()
      .single()
    return healthLogFromRow(assertRowReturned(data, error) as HealthLogRow)
  },

  async getDaily(date): Promise<DailyHealth | null> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('daily_health')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .maybeSingle()
    if (error) fail(error)
    return data ? dailyHealthFromRow(data as DailyHealthRow) : null
  },

  async saveDaily(input) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('daily_health')
      .upsert(
        {
          user_id: userId,
          date: input.date,
          hydration: input.hydration,
          sleep_hours: input.sleepHours,
          sleep_quality: input.sleepQuality,
          notes: input.notes,
        },
        { onConflict: 'user_id,date' },
      )
      .select()
      .single()
    return dailyHealthFromRow(assertRowReturned(data, error) as DailyHealthRow)
  },

  async listRecentDaily(days) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const since = new Date()
    since.setDate(since.getDate() - Math.max(0, days - 1))
    const sinceDate = since.toISOString().slice(0, 10)
    const { data, error } = await supabase
      .from('daily_health')
      .select('*')
      .eq('user_id', userId)
      .gte('date', sinceDate)
      .order('date', { ascending: false })
    if (error) fail(error)
    return ((data ?? []) as DailyHealthRow[]).map(dailyHealthFromRow)
  },
}
